/**
 * src/FreeUtilitiesPage.js
 *
 * VISINVOKE Free Utilities - dedicated dashboard collection page.
 *
 * Destination page for the "Free VISINVOKE Utilities" collection card on the
 * Utilities landing page (/dashboard/utilities). It holds the approved free
 * utility catalogue: the current genuine full free utilities, grouped into
 * curated collections with a compact, product-led hierarchy.
 *
 * Access model:
 *   - Every authenticated VISINVOKE user may browse this collection.
 *   - Most products are accessed through the Desktop Command Center. The two
 *     products with approved browser builds retain their browser launch action.
 *   - These are full free utilities, not trials and not limited editions.
 *
 * The product data (FREE_UTILITIES) comes from UtilitiesPage.js so this page and
 * the rest of the Utilities area retain one source of truth.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import MoreInfoPill from './components/MoreInfoPill';
import { FREE_UTILITIES } from './UtilitiesPage';
import { openDataStatusLabel, useOpenDataReleaseState } from './config/openDataRelease';

// Page-specific layout. The global Level 2 glass language and ProductNav remain
// untouched; these classes only reshape the Free Utilities catalogue into wider,
// shorter, grouped product panels.
const FU_GRID_CSS = `
  .fu-value-rail {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1px;
    margin: 30px 0 0;
    overflow: hidden;
    border: 1px solid rgba(47,125,255,0.22);
    border-radius: 18px;
    background: rgba(47,125,255,0.18);
    box-shadow: 0 14px 40px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.04);
    text-align: left;
  }
  .fu-value-item {
    position: relative;
    min-width: 0;
    padding: 24px 26px 25px;
    background: linear-gradient(155deg, rgba(16,26,46,0.97), rgba(10,17,30,0.98));
  }
  .fu-value-item::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(circle at 0 0, var(--value-accent-soft), transparent 45%);
  }
  .fu-value-copy { position: relative; z-index: 1; }

  .fu-catalogue {
    max-width: 1760px;
    margin: 0 auto;
    padding: 18px 40px 10px;
    width: 100%;
    box-sizing: border-box;
  }
  .fu-product-section {
    position: relative;
    padding: 38px 0 4px;
  }
  .fu-product-section + .fu-product-section {
    margin-top: 18px;
    border-top: 1px solid rgba(255,255,255,0.07);
  }
  .fu-section-head {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(300px, 620px);
    gap: 36px;
    align-items: stretch;
    margin-bottom: 20px;
  }
  .fu-section-rule {
    width: 46px;
    height: 3px;
    border-radius: 999px;
    margin-bottom: 12px;
    background: var(--section-accent);
    box-shadow: 0 0 18px var(--section-accent-soft);
  }
  .fu-product-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    align-items: stretch;
  }
  .fu-product-grid > .fu-product-card:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }
  .fu-product-card {
    position: relative;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    padding: 25px 26px 22px;
    border-radius: 18px;
    background:
      radial-gradient(circle at 0 0, var(--accent-soft) 0%, transparent 42%),
      linear-gradient(150deg, rgba(20,30,49,0.97) 0%, rgba(10,17,29,0.98) 68%, rgba(8,13,23,0.99) 100%);
    border: 1px solid var(--accent-border);
    box-shadow: 0 12px 38px rgba(0,0,0,0.44), inset 0 1px 0 rgba(255,255,255,0.055), 0 0 24px var(--accent-glow);
    transition: transform 220ms cubic-bezier(0.22,1,0.36,1), box-shadow 220ms ease, border-color 220ms ease;
    text-align: left;
    box-sizing: border-box;
  }
  .fu-product-card:hover {
    transform: translateY(-3px);
    border-color: var(--accent-strong);
    box-shadow: 0 18px 48px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08), 0 0 34px var(--accent-glow-hover);
  }
  .fu-product-card::before {
    content: '';
    position: absolute;
    inset: 0 0 auto;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--accent), transparent);
    opacity: 0.8;
  }
  .fu-product-card::after {
    content: '';
    position: absolute;
    width: 180px;
    height: 180px;
    right: -88px;
    top: -110px;
    border-radius: 50%;
    border: 1px solid var(--accent-border);
    box-shadow: 0 0 70px var(--accent-glow);
    pointer-events: none;
  }
  .fu-product-card--featured {
    padding: 28px 29px 24px;
    background:
      radial-gradient(circle at 4% 0%, var(--accent-soft) 0%, transparent 46%),
      linear-gradient(145deg, rgba(24,35,57,0.98), rgba(9,16,28,0.99) 72%);
  }
  .fu-card-top {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 18px;
  }
  .fu-card-identity { display: flex; align-items: center; gap: 13px; min-width: 0; }
  .fu-card-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    font-size: 25px;
    background: var(--accent-soft);
    border: 1px solid var(--accent-border);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.08), 0 8px 24px var(--accent-glow);
  }
  .fu-card-layout {
    display: grid;
    grid-template-columns: minmax(0, 0.94fr) minmax(250px, 1.06fr);
    gap: 28px;
    align-items: start;
    position: relative;
    z-index: 1;
  }
  .fu-card-benefits {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 9px;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .fu-card-benefit {
    min-width: 0;
    padding: 12px 12px 11px;
    border-radius: 12px;
    background: rgba(255,255,255,0.045);
    border: 1px solid rgba(255,255,255,0.09);
    color: rgba(255,255,255,0.9);
    font-size: 12.6px;
    line-height: 1.42;
  }
  .fu-card-benefit::before {
    content: '';
    display: block;
    width: 18px;
    height: 2px;
    margin-bottom: 9px;
    border-radius: 999px;
    background: var(--accent);
    box-shadow: 0 0 10px var(--accent-glow-hover);
  }
  .fu-card-footer {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px 20px;
    flex-wrap: wrap;
    margin-top: 20px;
    padding-top: 17px;
    border-top: 1px solid rgba(255,255,255,0.095);
  }
  .fu-access-cluster, .fu-action-cluster {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .fu-access-pill, .fu-launch-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 34px;
    box-sizing: border-box;
    border-radius: 999px;
    padding: 8px 12px;
    border: 1px solid rgba(255,255,255,0.14);
    background: rgba(255,255,255,0.055);
    color: rgba(255,255,255,0.82);
    font-size: 10.5px;
    font-weight: 850;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .fu-launch-button {
    cursor: pointer;
    border-color: var(--accent-strong);
    background: var(--accent);
    color: #07101d;
    box-shadow: 0 6px 20px var(--accent-glow-hover);
    transition: transform 160ms ease, filter 160ms ease;
  }
  .fu-launch-button:hover { transform: translateY(-1px); filter: brightness(1.08); }
  .fu-card-addon {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 9px;
    margin-top: 14px;
    padding: 10px 12px;
    border-radius: 12px;
    text-decoration: none;
    background: rgba(52,211,153,0.09);
    border: 1px solid rgba(52,211,153,0.28);
    color: #d7f7e8;
    font-size: 12px;
    line-height: 1.4;
  }
  .fu-close-panel {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1.25fr) minmax(390px, 0.75fr);
    gap: 34px;
    overflow: hidden;
    border-radius: 24px;
    padding: 42px;
    border: 1px solid rgba(77,152,255,0.25);
    background:
      radial-gradient(circle at 8% 0%, rgba(77,152,255,0.18), transparent 34%),
      radial-gradient(circle at 100% 100%, rgba(255,221,0,0.09), transparent 34%),
      linear-gradient(145deg, rgba(16,25,43,0.98), rgba(7,13,23,0.99));
    box-shadow: 0 24px 70px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.055);
  }
  .fu-close-panel::before {
    content: '';
    position: absolute;
    inset: 0 0 auto;
    height: 3px;
    background: linear-gradient(90deg, #4D98FF, #34D399 48%, #FFDD00);
  }
  .fu-close-points {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 25px;
  }
  .fu-close-point {
    padding: 16px 17px;
    border-radius: 14px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
  }
  .fu-studio-spotlight {
    position: relative;
    z-index: 1;
    padding: 26px;
    border-radius: 18px;
    background: linear-gradient(155deg, rgba(83,65,7,0.5), rgba(22,23,24,0.82));
    border: 1px solid rgba(255,221,0,0.28);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.07), 0 16px 42px rgba(0,0,0,0.32);
  }

  @media (max-width: 1180px) {
    .fu-section-head { grid-template-columns: 1fr; gap: 10px; }
    .fu-card-layout { grid-template-columns: 1fr; gap: 18px; }
    .fu-card-benefits { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .fu-close-panel { grid-template-columns: 1fr; }
  }
  @media (max-width: 1000px) {
    .fu-value-rail { grid-template-columns: 1fr; gap: 1px; }
    .fu-product-grid { grid-template-columns: 1fr; }
    .fu-product-grid > .fu-product-card:last-child:nth-child(odd) { grid-column: auto; }
  }
  @media (max-width: 700px) {
    .fu-page-shell { padding-left: 20px !important; padding-right: 20px !important; }
    .fu-catalogue { padding-left: 20px; padding-right: 20px; }
    .fu-page-title { font-size: 36px !important; letter-spacing: -0.55px !important; }
    .fu-page-value { font-size: 26px !important; }
    .fu-value-item { padding: 21px 20px 22px; }
    .fu-product-section { padding-top: 30px; }
    .fu-product-card, .fu-product-card--featured { padding: 22px 20px 20px; }
  }
  /* Angled software-value badge. Base = stacked/centred above the hero (small screens);
     on wide screens it lifts into the empty space to the LEFT of the centred hero. */
  .fu-value-badge {
    position: relative;
    width: 300px;
    margin: 0 auto 24px;
    transform: rotate(-4deg);
    padding: 26px 24px 22px;
    border-radius: 20px;
    background: linear-gradient(145deg,#FFE763 0%,#FFD400 34%,#F5B300 72%,#E39400 100%);
    border: 1px solid rgba(255,255,255,0.55);
    box-shadow: 0 26px 60px rgba(0,0,0,0.55), 0 5px 0 rgba(90,60,0,0.30), inset 0 2px 0 rgba(255,255,255,0.75), inset 0 -12px 26px rgba(150,90,0,0.28);
    color: #1a1400;
    text-align: center;
  }
  .fu-value-badge::before {
    content: ''; position: absolute; inset: 8px; border-radius: 14px;
    border: 1px solid rgba(120,80,0,0.30); pointer-events: none;
  }
  .fvb-amount {
    font-size: 80px; font-weight: 900; line-height: 0.92; letter-spacing: -2px;
    text-shadow: 0 1px 0 rgba(255,255,255,0.55), 0 3px 6px rgba(120,80,0,0.35);
  }
  .fvb-plus { font-size: 46px; vertical-align: super; margin-left: 2px; }
  .fvb-label { margin-top: 8px; font-size: 20px; font-weight: 900; letter-spacing: 2px; }
  .fvb-sub { margin-top: 10px; font-size: 12.5px; font-weight: 800; letter-spacing: 0.6px; color: #3a2c00; }
  @media (min-width: 1440px) {
    .fu-value-badge {
      position: absolute; left: 24px; top: 50%;
      transform: translateY(-50%) rotate(-6deg);
      margin: 0;
    }
  }
  @media (max-width: 520px) {
    .fu-value-badge { width: 86%; box-sizing: border-box; padding-left: 16px; padding-right: 16px; }
    .fvb-amount { font-size: 66px; }
    .fu-card-benefits { grid-template-columns: 1fr; }
    .fu-card-top { flex-direction: column; }
    .fu-card-footer, .fu-access-cluster, .fu-action-cluster { align-items: stretch; }
    .fu-card-footer { flex-direction: column; }
    .fu-access-cluster, .fu-action-cluster { width: 100%; }
    .fu-access-pill, .fu-launch-button { flex: 1 1 auto; }
    .fu-close-points { grid-template-columns: 1fr; }
    .fu-close-panel { padding: 30px 20px 22px; }
  }
`;

// Authoritative published path for the Presentation Studio Business & Security PDF. It ships with the
// portal build from public/presentation-studio/ (same origin as the browser build), so no separate infra.
const PS_BS_PDF = '/presentation-studio/VISINVOKE-Presentation-Studio-Business-and-Security.pdf';

const PRODUCT_GROUPS = [
  {
    key: 'signature',
    eyebrow: 'CURATED HIGHLIGHTS',
    title: 'Signature free products',
    intro: 'Four standout tools that show the range of the collection — serious conversion, creative production and a better Windows workspace.',
    accent: '#FFDD00',
    featured: true,
    products: [
      'VISINVOKE File Converter',
      'VISINVOKE Presentation Studio',
      'VISINVOKE Image Composer',
      'VISINVOKE Screen Manager',
    ],
  },
  {
    key: 'essentials',
    eyebrow: 'EVERYDAY ESSENTIALS',
    title: 'Protect, prepare and move your files',
    intro: 'Practical local tools for the work people repeat every day: backups, documents, images and managed USB media.',
    accent: '#4D98FF',
    products: [
      'VISINVOKE Backup Utility',
      'VISINVOKE PDF Toolkit',
      'VISINVOKE Image Toolkit',
      'VISINVOKE USB Builder',
    ],
  },
  {
    key: 'discovery',
    eyebrow: 'DISCOVERY & REFERENCE',
    title: 'Explore information with the source still visible',
    intro: 'Connected explorers for people, animals, media, music and the world — designed around provenance, clarity and useful comparison.',
    accent: '#34D399',
    products: [
      'VISINVOKE People Explorer',
      'VISINVOKE Animal Explorer',
      'VISINVOKE Plex Discovery',
      'VISINVOKE Music Explorer',
      'VISINVOKE World Explorer',
      'VISINVOKE Books Explorer',
    ],
  },
  {
    key: 'workflow',
    eyebrow: 'WORKSTATION & WORKFLOW',
    title: 'Bring order to the way you work',
    intro: 'Focused desktop products for communication and agent control, with local ownership and a deliberately clear operating model.',
    accent: '#22D3EE',
    products: [
      'VISINVOKE Email',
      'VISINVOKE Agent Control',
    ],
  },
];

const CLOSEOUT_POINTS = [
  { icon: '◆', title: 'Full versions', body: 'Complete products for ongoing use — not demos, trials or deliberately reduced editions.' },
  { icon: '◈', title: 'Local by design', body: 'Most processing happens on your computer, keeping normal work direct and private.' },
  { icon: '◇', title: 'One VISINVOKE account', body: 'A single account connects the catalogue, DCC access and product information.' },
  { icon: '▸', title: 'Browser when useful', body: 'Presentation Studio and Image Composer can also open directly in your browser.' },
];

const PRODUCT_LOOKUP = FREE_UTILITIES.reduce((result, product) => {
  result[product.title] = product;
  return result;
}, {});

function alpha(hex, opacity) {
  const value = String(hex || '#94a3b8').replace('#', '');
  if (value.length !== 6) return `rgba(148,163,184,${opacity})`;
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);
  return `rgba(${red},${green},${blue},${opacity})`;
}

// Three value cards headlining the proposition, between the VISINVOKE MEANS VALUE artwork and the catalogue.
const VALUE_CARDS = [
  { accent: '#FFDD00', kicker: '01',
    head: 'Full version \u00b7 Free forever',
    body: 'Fifteen complete VISINVOKE products, yours permanently. No trials, no expiry and no deliberately locked features \u2014 and no presentation, conversion or other credits to spend during normal use. Free here means the full product, not a cut-down taster.' },
  { accent: '#4D98FF', kicker: '02',
    head: 'High-quality software',
    body: 'Serious VISINVOKE applications, built to the same standard as the rest of the ecosystem. Each does substantial, genuinely useful work \u2014 tools you can rely on day to day, not token freebies added to advertise something paid.' },
  { accent: '#34D399', kicker: '03',
    head: 'Your computer does the work',
    body: 'The utilities do their actual work on your own computer, so your files stay with you and normal use does not depend on VISINVOKE servers. Most run through the Desktop Command Center; Presentation Studio also runs straight in your browser.' },
];

export function PremiumUtilityCard({ product, featured }) {
  const accent = product.accent || '#94a3b8';
  const points = Array.isArray(product.points) ? product.points : [];
  const visiblePoints = points.slice(0, 3);
  const remainingPoints = Math.max(0, points.length - visiblePoints.length);
  const available = product.status === 'Available';
  const availabilityLabel = available ? 'AVAILABLE NOW' : String(product.status || 'COMING SOON').toUpperCase();
  const availabilityColor = available ? '#bbf7d0' : '#fde68a';

  const variables = {
    '--accent': accent,
    '--accent-soft': alpha(accent, featured ? 0.24 : 0.17),
    '--accent-border': alpha(accent, featured ? 0.43 : 0.3),
    '--accent-strong': alpha(accent, 0.72),
    '--accent-glow': alpha(accent, featured ? 0.18 : 0.12),
    '--accent-glow-hover': alpha(accent, featured ? 0.32 : 0.24),
  };

  return (
    <article
      className={`fu-product-card${featured ? ' fu-product-card--featured' : ''}`}
      style={variables}
      aria-label={product.title}
    >
      <div className="fu-card-top">
        <div className="fu-card-identity">
          <div className="fu-card-icon" aria-hidden="true">{product.icon}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...fu.cardEyebrow, color: accent }}>
              {featured ? 'Signature free product' : 'Full-version utility'}
            </div>
            <h3 style={fu.cardTitle}>{product.title}</h3>
          </div>
        </div>
        <div style={fu.cardStatusRow}>
          <span style={fu.cardFreePill}>FREE</span>
          <span style={{ ...fu.cardStatusPill, color: availabilityColor, borderColor: alpha(availabilityColor, 0.34), background: alpha(availabilityColor, 0.08) }}>
            <span style={{ ...fu.cardStatusDot, background: availabilityColor }} />
            {availabilityLabel}
          </span>
        </div>
      </div>

      <div className="fu-card-layout">
        <div>
          <p style={fu.cardDescription}>{product.description}</p>
          <div style={fu.cardIncludedNote}>Included with your VISINVOKE account · full utility, not a trial</div>
        </div>
        <div>
          <div style={fu.cardPointsHead}>
            <span>{product.pointsLabel || 'Highlights'}</span>
            {remainingPoints > 0 ? <span style={fu.cardMoreCount}>+{remainingPoints} more</span> : null}
          </div>
          <ul className="fu-card-benefits">
            {visiblePoints.map((point, index) => (
              <li className="fu-card-benefit" key={index}>{point}</li>
            ))}
          </ul>
        </div>
      </div>

      {product.paidAddon ? (
        <a className="fu-card-addon" href={product.paidAddon.href}>
          <span style={{ color: '#34d399', fontSize: 15 }}>{'☁'}</span>
          <span style={{ flex: 1 }}><strong>Optional paid AI Rebuild</strong> · {product.paidAddon.note}</span>
          <span style={{ color: '#34d399', fontWeight: 900 }}>{'→'}</span>
        </a>
      ) : null}

      <div className="fu-card-footer">
        <div className="fu-access-cluster" aria-label={`${product.title} access methods`}>
          <span className="fu-access-pill"><span aria-hidden="true">{'🖥'}</span>DCC access</span>
          {product.launch ? <span className="fu-access-pill"><span aria-hidden="true">{'🌐'}</span>Browser access</span> : null}
        </div>
        <div className="fu-action-cluster">
          {product.launch ? (
            <button
              type="button"
              className="fu-launch-button"
              onClick={() => { if (typeof window !== 'undefined') window.open(product.launch.url, '_blank', 'noopener'); }}
              aria-label={`Launch ${product.title} in your browser`}
            >
              <span aria-hidden="true">{'▸'}</span>{product.launch.label || 'LAUNCH IN BROWSER'}
            </button>
          ) : null}
          {product.detailKey ? (
            <MoreInfoPill
              productKey={product.detailKey}
              accent={accent}
              style={{ width: 'auto', minHeight: 34, padding: '8px 14px', fontSize: 10.5, flex: '1 1 auto' }}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
}

export default function FreeUtilitiesPage() {
  const openDataRelease = useOpenDataReleaseState();
  const navigate = useNavigate();

  useEffect(() => {
    let style = document.getElementById('fu-grid-css');
    if (!style) {
      style = document.createElement('style');
      style.id = 'fu-grid-css';
      document.head.appendChild(style);
    }
    style.textContent = FU_GRID_CSS;
  }, []);

  return (
    <div style={fu.page}>
      <ProductNav />

      {/* Back to Utilities */}
      <div className="vi-desktop-wide fu-page-shell" style={fu.backWrap}>
        <button
          style={fu.backLink}
          onClick={() => navigate('/dashboard/utilities')}
          onMouseEnter={e => { e.currentTarget.style.color = '#4D98FF'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#aab8c9'; }}
        >
          {'←'} VISINVOKE Utilities
        </button>
      </div>

      {/* Hero / intro */}
      <div className="vi-desktop-wide fu-page-shell" style={fu.intro}>
        {/* Hero: existing centred block, unchanged, with the angled software-value badge lifted into the empty space to its left on wide screens. */}
        <div className="fu-hero" style={fu.hero}>
          <div className="fu-value-badge" aria-label="Combined software value: over 1,100 dollars — full versions, free forever">
            <div className="fvb-amount">$1,100<span className="fvb-plus">+</span></div>
            <div className="fvb-label">SOFTWARE VALUE</div>
            <div className="fvb-sub">FULL VERSIONS &middot; FREE FOREVER</div>
          </div>
          {/* Supplied VISINVOKE MEANS VALUE artwork — used as-is: large, centred, aspect preserved, responsive. */}
          <img
            src="/visinvoke-means-value.png"
            alt="VISINVOKE means value — Quality. Ownership. Rewards."
            style={fu.introImg}
          />
          <h1 className="fu-page-title" style={fu.introTitle}>FREE VISINVOKE UTILITIES</h1>
          <p className="fu-page-value" style={fu.introValue}>
            Fifteen <strong style={fu.introEm}>full-version</strong> VISINVOKE products.{' '}
            <strong style={fu.introEm}>Free forever.</strong>
          </p>
          <p style={fu.introValueSub}>
            High-quality VISINVOKE software &mdash; not trials, not time-limited, not deliberately feature-restricted.
          </p>
        </div>

        {/* A compact value rail keeps the proposition visible without adding another row of tall cards. */}
        <div className="fu-value-rail">
          {VALUE_CARDS.map((c, i) => (
            <div key={i} className="fu-value-item" style={{ '--value-accent-soft': alpha(c.accent, 0.15) }}>
              <div className="fu-value-copy">
                <div style={{ ...fu.valueRailKicker, color: c.accent }}>{c.kicker}</div>
                <h3 style={fu.valueRailHead}>{c.head}</h3>
                <p style={fu.valueRailBody}>{c.body}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={fu.dccNote}>
          <span style={fu.dccIcon}>{'\u{1F5A5}'}</span>
          <span>
            Most utilities below run through the VISINVOKE <strong style={fu.dccStrong}>Desktop Command Center</strong>.
            Presentation Studio runs directly in your browser — nothing is installed from this page.
          </span>
        </div>
      </div>

      {/* Deliberate product collections replace the former undifferentiated wall of cards. */}
      <main className="fu-catalogue vi-desktop-wide" aria-label="Free VISINVOKE Utilities catalogue">
        {PRODUCT_GROUPS.map((group, groupIndex) => {
          const products = group.products.map(title => PRODUCT_LOOKUP[title]).filter(Boolean)
            .map(product => (product.releaseGate === 'open-data'
              ? { ...product, status: openDataStatusLabel(openDataRelease, product.detailKey, 'dashboard') }
              : product));
          return (
            <section
              className="fu-product-section"
              key={group.key}
              aria-labelledby={`fu-section-${group.key}`}
              style={{
                '--section-accent': group.accent,
                '--section-accent-soft': alpha(group.accent, 0.32),
              }}
            >
              <div className="fu-section-head">
                <div>
                  <div className="fu-section-rule" />
                  <div style={{ ...fu.sectionEyebrow, color: group.accent }}>
                    {String(groupIndex + 1).padStart(2, '0')} · {group.eyebrow}
                  </div>
                  <h2 id={`fu-section-${group.key}`} style={fu.sectionTitle}>{group.title}</h2>
                </div>
                <p style={fu.sectionIntro}>{group.intro}</p>
              </div>
              <div className="fu-product-grid">
                {products.map(product => (
                  <PremiumUtilityCard key={product.title} product={product} featured={!!group.featured} />
                ))}
              </div>
            </section>
          );
        })}
      </main>

      {/* Premium close: the collection promise and Presentation Studio trust details now finish together. */}
      <div className="vi-desktop-wide fu-page-shell" style={fu.closeWrap}>
        <section className="fu-close-panel" aria-label="The VISINVOKE free software promise">
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={fu.closeEyebrow}>THE FREE SOFTWARE PROMISE</div>
            <h2 style={fu.closeTitle}>Fifteen products. One generous standard.</h2>
            <p style={fu.closeLead}>
              Useful software should feel complete from the first day. VISINVOKE Free Utilities combines
              business-ready tools, private local processing and clear access through the Desktop Command Center,
              with browser access where it makes the experience better.
            </p>
            <div className="fu-close-points">
              {CLOSEOUT_POINTS.map(point => (
                <div className="fu-close-point" key={point.title}>
                  <div style={fu.closePointTitle}><span style={fu.closePointIcon}>{point.icon}</span>{point.title}</div>
                  <div style={fu.closePointBody}>{point.body}</div>
                </div>
              ))}
            </div>
          </div>

          <aside className="fu-studio-spotlight" aria-label="Presentation Studio Business and Security">
            <div style={fu.studioKicker}>PRESENTATION STUDIO</div>
            <h3 style={fu.studioTitle}>Business-ready. Local. Editable.</h3>
            <p style={fu.studioBody}>
              Build management, reporting and training presentations from PowerPoint, spreadsheet or pasted data.
              Your working files stay on your computer, and PowerPoint export remains fully editable.
            </p>
            <ul style={fu.studioList}>
              <li style={fu.studioListItem}>Local processing with autosave and recovery</li>
              <li style={fu.studioListItem}>PPTX, XLSX, CSV, TSV and pasted-data import</li>
              <li style={fu.studioListItem}>Editable PowerPoint plus PDF and image export</li>
            </ul>
            <a
              href={PS_BS_PDF}
              target="_blank"
              rel="noopener noreferrer"
              style={fu.studioPdfButton}
              onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
            >
              <span aria-hidden="true">{'↓'}</span> Business &amp; Security PDF
            </a>
          </aside>
        </section>
      </div>
    </div>
  );
}

const fu = {
  page: {
    minHeight: '100vh',
    // Dark page owns the full background. paddingBottom holds what used to be the last child's
    // bottom margin, keeping it INSIDE the painted box instead of collapsing out and exposing the
    // white #root/body beneath it. box-sizing keeps minHeight:100vh honest (padding counted in).
    boxSizing: 'border-box',
    paddingBottom: 84,
    background: 'radial-gradient(1200px 640px at 50% -4%, rgba(47,125,255,0.09) 0%, rgba(0,0,0,0) 44%), radial-gradient(1000px 720px at 100% 112%, rgba(47,125,255,0.05) 0%, rgba(0,0,0,0) 55%), #080B11',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  backWrap: {
    maxWidth: 1760,
    margin: '0 auto',
    padding: '22px 40px 0',
    width: '100%',
    boxSizing: 'border-box',
  },
  backLink: {
    background: 'transparent',
    border: 'none',
    color: '#aab8c9',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    transition: 'color 180ms ease',
  },
  intro: {
    maxWidth: 1760,
    margin: '0 auto',
    padding: '14px 40px 0',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'center',
  },
  introEyebrow: {
    fontSize: 13,
    fontWeight: 800,
    color: '#78ADFF',
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    margin: '0 0 12px',
  },
  hero: {
    position: 'relative',
  },
  introImg: {
    display: 'block',
    width: '100%',
    maxWidth: 600,
    height: 'auto',
    margin: '4px auto 12px',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 18px 60px rgba(0,0,0,0.5)',
  },
  introTitle: {
    fontSize: 46,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.8px',
    lineHeight: 1.08,
    margin: 0,
  },
  introValue: {
    fontSize: 32,
    fontWeight: 900,
    color: '#ffffff',
    lineHeight: 1.2,
    letterSpacing: '-0.4px',
    margin: '16px auto 0',
    maxWidth: 1100,
  },
  introValueSub: {
    fontSize: 18,
    fontWeight: 600,
    color: '#cbd5e1',
    lineHeight: 1.5,
    margin: '12px auto 0',
    maxWidth: 900,
  },
  introEm: {
    color: '#FFDD00',
    fontWeight: 900,
  },
  valueRailKicker: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: '0.16em',
    lineHeight: 1,
    marginBottom: 10,
  },
  valueRailHead: {
    fontSize: 20,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.22px',
    lineHeight: 1.2,
    margin: '0 0 9px',
  },
  valueRailBody: {
    fontSize: 13.5,
    color: '#cbd5e1',
    lineHeight: 1.55,
    margin: 0,
  },
  cardEyebrow: {
    fontSize: 10.5,
    fontWeight: 900,
    letterSpacing: '0.13em',
    lineHeight: 1.2,
    textTransform: 'uppercase',
    marginBottom: 5,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.25px',
    lineHeight: 1.18,
    margin: 0,
  },
  cardStatusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 7,
    flexWrap: 'wrap',
  },
  cardFreePill: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 27,
    padding: '6px 10px',
    borderRadius: 999,
    background: '#ffffff',
    color: '#07101d',
    fontSize: 10,
    fontWeight: 950,
    letterSpacing: '0.1em',
  },
  cardStatusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 27,
    boxSizing: 'border-box',
    padding: '5px 9px',
    borderRadius: 999,
    border: '1px solid',
    fontSize: 9.5,
    fontWeight: 900,
    letterSpacing: '0.08em',
  },
  cardStatusDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    flex: '0 0 auto',
    boxShadow: '0 0 10px currentColor',
  },
  cardDescription: {
    fontSize: 14.2,
    color: '#e6edf5',
    lineHeight: 1.58,
    margin: 0,
  },
  cardIncludedNote: {
    marginTop: 12,
    color: 'rgba(255,255,255,0.52)',
    fontSize: 11.5,
    fontWeight: 750,
    letterSpacing: '0.01em',
  },
  cardPointsHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 9,
    color: 'rgba(255,255,255,0.58)',
    fontSize: 10.5,
    fontWeight: 900,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  cardMoreCount: {
    color: 'rgba(255,255,255,0.42)',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  },
  sectionEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    marginBottom: 7,
  },
  sectionTitle: {
    fontSize: 30,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.55px',
    lineHeight: 1.13,
    margin: 0,
  },
  sectionIntro: {
    alignSelf: 'end',
    maxWidth: 620,
    color: '#aebdce',
    fontSize: 14.5,
    lineHeight: 1.62,
    margin: 0,
  },
  dccNote: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    width: '100%',
    margin: '26px 0 0',
    padding: '20px 26px',
    borderRadius: 14,
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(47,125,255,0.30)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)',
    fontSize: 17,
    color: '#d3dbe8',
    lineHeight: 1.55,
    textAlign: 'left',
    boxSizing: 'border-box',
  },
  dccIcon: {
    fontSize: 26,
    flexShrink: 0,
    filter: 'drop-shadow(0 0 10px rgba(47,125,255,0.4))',
  },
  dccStrong: {
    color: '#ffffff',
    fontWeight: 800,
  },
  closeWrap: {
    maxWidth: 1760,
    margin: '34px auto 0',
    padding: '0 40px',
    width: '100%',
    boxSizing: 'border-box',
  },
  closeEyebrow: {
    color: '#78ADFF',
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: '0.17em',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  closeTitle: {
    maxWidth: 760,
    color: '#ffffff',
    fontSize: 36,
    fontWeight: 900,
    letterSpacing: '-0.7px',
    lineHeight: 1.1,
    margin: 0,
  },
  closeLead: {
    maxWidth: 780,
    color: '#c8d3df',
    fontSize: 15,
    lineHeight: 1.65,
    margin: '15px 0 0',
  },
  closePointTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    color: '#ffffff',
    fontSize: 13.5,
    fontWeight: 900,
    marginBottom: 7,
  },
  closePointIcon: {
    color: '#4D98FF',
    fontSize: 12,
    textShadow: '0 0 12px rgba(77,152,255,0.65)',
  },
  closePointBody: {
    color: '#aebdce',
    fontSize: 12.5,
    lineHeight: 1.52,
  },
  studioKicker: {
    color: '#FFDD00',
    fontSize: 10.5,
    fontWeight: 900,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  studioTitle: {
    color: '#ffffff',
    fontSize: 24,
    fontWeight: 900,
    letterSpacing: '-0.35px',
    lineHeight: 1.18,
    margin: 0,
  },
  studioBody: {
    color: '#d5dbe3',
    fontSize: 13.5,
    lineHeight: 1.6,
    margin: '12px 0 0',
  },
  studioList: {
    display: 'grid',
    gap: 8,
    listStyle: 'none',
    margin: '18px 0 20px',
    padding: 0,
  },
  studioListItem: {
    padding: '9px 11px',
    borderLeft: '2px solid #FFDD00',
    borderRadius: '0 9px 9px 0',
    background: 'rgba(255,255,255,0.045)',
    color: '#e4e8ed',
    fontSize: 12.5,
    lineHeight: 1.42,
  },
  studioPdfButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    minHeight: 42,
    boxSizing: 'border-box',
    padding: '11px 18px',
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.5)',
    background: '#FFDD00',
    boxShadow: '0 7px 24px rgba(245,184,0,0.34)',
    color: '#1a1500',
    fontSize: 12.5,
    fontWeight: 900,
    letterSpacing: '0.03em',
    textDecoration: 'none',
    transition: 'filter 160ms ease',
  },
};
